export const URL_PATTERN =
  /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i;

export const PHONE_PATTERN = /^\+?[1-9]\d{1,14}$/;

export const TEMPLATE_BODY_MAX = 1024;
export const CARD_BODY_MAX = 160;
export const BUTTON_TEXT_MAX = 25;
export const CAROUSEL_MIN_CARDS = 2;
export const CAROUSEL_MAX_CARDS = 10;
export const CAROUSEL_MAX_BUTTONS = 2;

export function isValidUrl(value) {
  return URL_PATTERN.test((value || '').trim());
}

export function isValidPhone(value) {
  const cleanPhone = (value || '').replace(/[\s-]/g, '');
  return PHONE_PATTERN.test(cleanPhone);
}

export function getButtonFieldError(btn) {
  const val = (btn.value || '').trim();
  if (!val) return '';
  if (btn.type === 'url' && !isValidUrl(val)) {
    return 'Please enter a valid URL (e.g. https://example.com)';
  }
  if (btn.type === 'phone' && !isValidPhone(val)) {
    return 'Please enter a valid phone number (e.g. +919876543210)';
  }
  return '';
}

export function validateButtonList(buttons, { labelPrefix = 'Button' } = {}) {
  for (let i = 0; i < buttons.length; i++) {
    const btn = buttons[i];
    const btnNum = i + 1;
    const label = `${labelPrefix} #${btnNum}`;

    if (!btn.text?.trim()) {
      return `Please enter text for ${label}.`;
    }

    if (btn.text.length > BUTTON_TEXT_MAX) {
      return `${label} text cannot exceed ${BUTTON_TEXT_MAX} characters.`;
    }

    if (btn.type === 'url') {
      if (!btn.value?.trim()) {
        return `Please enter a URL address for ${label}.`;
      }
      if (!isValidUrl(btn.value)) {
        return `Please enter a valid URL for ${label} (e.g. https://example.com).`;
      }
    }

    if (btn.type === 'phone') {
      if (!btn.value?.trim()) {
        return `Please enter a phone number for ${label}.`;
      }
      if (!isValidPhone(btn.value)) {
        return `Please enter a valid phone number with country code for ${label} (e.g. +919876543210).`;
      }
    }
  }

  const quickReplyTexts = buttons
    .filter((b) => b.type === 'quick_reply')
    .map((b) => b.text.trim().toLowerCase());
  if (new Set(quickReplyTexts).size !== quickReplyTexts.length) {
    return 'Quick reply buttons must have unique text titles.';
  }

  return null;
}

export function validateCreateTemplate(formState) {
  const {
    templateName,
    language,
    category,
    bodyText,
    buttons = [],
    templateType,
    cards = [],
    cardHeaderType,
  } = formState;

  if (!templateName) {
    return 'Please enter a Template Name.';
  }
  if (!language) {
    return 'Please select a Language.';
  }
  if (!category) {
    return 'Please select a Category.';
  }
  if (category === 'Authentication') {
    const {
      otpType,
      expiryWarning,
      expiryMinutes,
      authButtonText,
      androidPackageName,
      appSignatureHash,
    } = formState;

    if (!otpType) {
      return 'Please select an OTP Type.';
    }

    if (expiryWarning) {
      if (
        !expiryMinutes ||
        isNaN(expiryMinutes) ||
        expiryMinutes < 1 ||
        expiryMinutes > 90
      ) {
        return 'Expiry minutes must be between 1 and 90.';
      }
    }

    if (!authButtonText?.trim()) {
      return 'Please enter the Button Text.';
    }

    if (authButtonText.length > BUTTON_TEXT_MAX) {
      return `Button text cannot exceed ${BUTTON_TEXT_MAX} characters.`;
    }

    if (otpType === 'One Tap Autofill') {
      if (!androidPackageName?.trim()) {
        return 'Please enter the Android Package Name.';
      }
      if (!appSignatureHash?.trim()) {
        return 'Please enter the App Signature Hash.';
      }
    }

    return null;
  }

  if (!bodyText?.trim()) {
    return 'Please enter the Body Text.';
  }
  if (bodyText.length > TEMPLATE_BODY_MAX) {
    return `Body text cannot exceed ${TEMPLATE_BODY_MAX} characters.`;
  }

  if (templateType !== 'Carousel') {
    return validateButtonList(buttons);
  }

  if (cards.length < CAROUSEL_MIN_CARDS || cards.length > CAROUSEL_MAX_CARDS) {
    return `Carousel templates must have between ${CAROUSEL_MIN_CARDS} and ${CAROUSEL_MAX_CARDS} cards.`;
  }

  const headerType = (
    cardHeaderType ||
    cards[0]?.header_type ||
    ''
  ).toUpperCase();
  if (headerType !== 'IMAGE' && headerType !== 'VIDEO') {
    return 'Please select IMAGE or VIDEO for carousel cards.';
  }

  if (
    cards.some((card) => (card.header_type || '').toUpperCase() !== headerType)
  ) {
    return 'All carousel cards must use the same header type.';
  }

  const firstButtonTypes = (cards[0].buttons || []).map((b) => b.type);
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    const cardNum = i + 1;

    if (!card.body_text?.trim()) {
      return `Please enter body text for Card #${cardNum}.`;
    }
    if (card.body_text.length > CARD_BODY_MAX) {
      return `Card #${cardNum} body text cannot exceed ${CARD_BODY_MAX} characters.`;
    }

    const hasMedia =
      headerType === 'IMAGE'
        ? Boolean(card.image_base64)
        : Boolean(card.video_base64);
    if (!hasMedia) {
      return `Please upload ${headerType === 'IMAGE' ? 'an image' : 'a video'} for Card #${cardNum}.`;
    }

    const cardButtons = card.buttons || [];
    if (cardButtons.length > CAROUSEL_MAX_BUTTONS) {
      return `Card #${cardNum} can have a maximum of ${CAROUSEL_MAX_BUTTONS} buttons.`;
    }

    if (cardButtons.length !== firstButtonTypes.length) {
      return 'All carousel cards must have the same number of buttons.';
    }

    for (let b = 0; b < cardButtons.length; b++) {
      if (cardButtons[b].type !== firstButtonTypes[b]) {
        return 'All carousel cards must have the same button types in the same order.';
      }
    }

    const buttonError = validateButtonList(cardButtons, {
      labelPrefix: `Card #${cardNum} button`,
    });
    if (buttonError) return buttonError;
  }

  return null;
}
